// src/pages/imoveis/Imoveis.tsx
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../services/apiService';
import { Imovel } from '../../data/Imovel';
import { ListaPaginada } from '../../data/ListaPaginada';
import Pagination from '../../componets/Pagination';
import ConfirmationModal from '../../componets/ConfirmationModal';

function Imoveis() {
  const [data, setData] = useState<ListaPaginada<Imovel> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [imovelToDelete, setImovelToDelete] = useState<number | null>(null);
  const itemsPerPage = 10; // Itens por página

  useEffect(() => {
    const fetchImoveis = async () => {
      setLoading(true);
      try {
        const endpoint = `/imovel?pagina=${currentPage}&tamanhoPagina=${itemsPerPage}`;
        const responseData = await apiRequest<ListaPaginada<Imovel>>(endpoint, 'GET', true);
        setData(responseData);
      } catch (err) {
        setError((err as Error).message || 'Erro ao carregar os imoveis.');
      } finally {
        setLoading(false);
      }
    };
    fetchImoveis();
  }, [currentPage]);

  const totalRecords = data?.totalItens ?? 0;
  const totalPages = data?.totalPaginas ?? 0;
  const currentItems = data?.itens ?? [];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Abre o modal de confirmação
  const handleDeleteClick = (id: number) => {
    setImovelToDelete(id);
    setShowDeleteModal(true);
  };

  // Executa a exclusão após a confirmação
  const confirmDelete = async () => {
    if (!imovelToDelete) return;

    setShowDeleteModal(false);
    setLoading(true);
    setError(null);

    try {
      // Assumindo que a API usa o método DELETE para exclusão de imóveis
      await apiRequest(`imovel/deleteimovel/${imovelToDelete}`, 'POST', true);

      // Recarrega os dados da página atual para refletir a exclusão
      const fetchImoveis = async () => {
        const endpoint = `/imovel?pagina=${currentPage}&tamanhoPagina=${itemsPerPage}`;
        const responseData = await apiRequest<ListaPaginada<Imovel>>(endpoint, 'GET', true);
        setData(responseData);
      };
      await fetchImoveis();
    } catch (err) {
      setError((err as Error).message || 'Erro ao excluir o imóvel.');
    } finally {
      setLoading(false);
      setImovelToDelete(null);
    }
  };

  if (loading) {
    return <p>Carregando Imoveis...</p>;
  }

  if (error) {
    return <p>Erro: {error}</p>;
  }

  return (
    <div className="row">
      <div className="card">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1 className="h3 mb-0 align-middle">Imóveis</h1>
            <Link to="/imoveis/novo" className="btn btn-primary"> Novo <i className="bi bi-plus"></i></Link>
          </div>
          <div className="row g-3 mb-4">
            {currentItems.length === 0 ? (
              <div className="col-12 text-center py-4">Nenhum imóvel cadastrado.</div>
            ) : (currentItems.map(imovel => {
              // Tenta resgatar a propriedade de destaque diretamente do JSON ou na lista de fotos, com um fallback visual
              const fotoUrl = (imovel as any).fotoDestaque || imovel.fotos?.find(f => f.destaque)?.url || imovel.fotos?.[0]?.url || 'https://via.placeholder.com/400x300?text=Sem+Foto';
              return (
                <div className="col-12" key={imovel.id}>
                  <div className="card h-100 border shadow-sm overflow-hidden">
                    <div className="row g-0 h-100">
                      <div className="col-sm-4">
                        <img 
                          src={fotoUrl} 
                          alt={imovel.titulo} 
                          className="img-fluid w-100 h-100" 
                          style={{ objectFit: 'cover', minHeight: '200px' }} 
                        />
                      </div>
                      <div className="col-sm-8">
                        <div className="card-body d-flex flex-column h-100">
                          <h5 className="card-title mb-1 text-truncate" title={imovel.titulo}>{imovel.titulo}</h5>
                          <span className="badge bg-secondary mb-2 align-self-start">#{imovel.id}</span>
                          <p className="card-text flex-grow-1 text-muted" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {imovel.descricao}
                          </p>
                          <div className="mt-3 d-flex gap-2 justify-content-end">
                            <Link to={`/imoveis/editar/${imovel.id}`} className="btn btn-outline-primary btn-sm"><i className="align-middle bi bi-pencil-square"></i> Editar</Link>
                            <button onClick={() => handleDeleteClick(imovel.id!)} className="btn btn-outline-danger btn-sm">
                              <i className="align-middle bi bi-trash"></i> Excluir
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }))}
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalRecords={totalRecords}
            onPageChange={handlePageChange}
          />
          <ConfirmationModal
            show={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={confirmDelete}
            title="Confirmar Exclusão"
            body="Tem certeza que deseja excluir este imóvel? Todos os dados e fotos associados a ele serão perdidos."
            confirmText="Excluir"
            confirmButtonVariant="danger"
          />
        </div>
      </div>
    </div>
  );
}

export default Imoveis;